import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useModeToggleAnimation } from '#pplx/frontend/composer-sdk-plugins/mode-toggle/internal/useModeToggleAnimation.js';
import { ModeToggle } from '#pplx/frontend/composer-sdk-plugins/mode-toggle/ModeToggle.js';
import { MODE_TOGGLE_CHEVRON_WIDTH_PX, MODE_TOGGLE_COLLAPSED_WIDTH_PX, } from '#pplx/frontend/composer-sdk-plugins/mode-toggle/types.js';
import { styles as surfaceStyles } from '#pplx_generated/pplx/frontend/composer-sdk/ComposerSurface.module.css.js';
import { animated, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
const NOOP_WIDTH_CHANGE = (_width) => { };
const NOOP_MENU_OPEN_CHANGE = (_open) => { };
function ToggleWidthWrapper({ isExpanded, usePersistentLabelLayout, activeWidthDeltaPx, animateChevronWidth, animationEnabled, springConfig, shouldAnimateWidth, onActiveWidthChange, onWidthChange, onInactiveHoverChange, testIdPrefix, children, }) {
    const contentRef = useRef(null);
    const [expandedWidth, setExpandedWidth] = useState(MODE_TOGGLE_COLLAPSED_WIDTH_PX);
    const [persistentBaseWidth, setPersistentBaseWidth] = useState(MODE_TOGGLE_COLLAPSED_WIDTH_PX);
    const { progress } = useSpring({
        progress: isExpanded && (!animateChevronWidth || activeWidthDeltaPx > 0) ? 1 : 0,
        immediate: !animationEnabled,
        config: animateChevronWidth ? { ...springConfig, precision: 0.0001 } : springConfig,
    });
    // Keep the same multiplier while a menu disappears so its remaining space
    // collapses with the caret, rather than jumping to zero before the spring.
    const persistentWidthDeltaPx = animateChevronWidth
        ? MODE_TOGGLE_CHEVRON_WIDTH_PX
        : activeWidthDeltaPx;
    const shouldUseContentWidth = isExpanded && !shouldAnimateWidth;
    const persistentTargetWidth = persistentBaseWidth + (isExpanded ? activeWidthDeltaPx : 0);
    useLayoutEffect(() => {
        if (animateChevronWidth)
            return;
        const element = contentRef.current;
        if (!element)
            return;
        const updateWidth = () => {
            const measuredWidth = element.scrollWidth;
            if (measuredWidth <= MODE_TOGGLE_COLLAPSED_WIDTH_PX)
                return;
            if (usePersistentLabelLayout) {
                setPersistentBaseWidth(Math.max(MODE_TOGGLE_COLLAPSED_WIDTH_PX, measuredWidth - (isExpanded ? activeWidthDeltaPx : 0)));
                return;
            }
            setExpandedWidth(measuredWidth);
        };
        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, [activeWidthDeltaPx, animateChevronWidth, isExpanded, usePersistentLabelLayout]);
    // Control: only the active toggle reports its width to the shared indicator.
    useLayoutEffect(() => {
        if (usePersistentLabelLayout)
            return;
        if (!isExpanded)
            return;
        onActiveWidthChange(expandedWidth);
    }, [usePersistentLabelLayout, expandedWidth, isExpanded, onActiveWidthChange]);
    // Report destination widths for the indicator. The caret and wrapper use
    // this toggle's same progress while framed labels remain visible.
    useLayoutEffect(() => {
        if (!usePersistentLabelLayout)
            return;
        onWidthChange(persistentTargetWidth);
    }, [usePersistentLabelLayout, onWidthChange, persistentTargetWidth]);
    useEffect(() => {
        if (isExpanded) {
            onInactiveHoverChange(false);
        }
    }, [isExpanded, onInactiveHoverChange]);
    return (_jsx(animated.span, { "data-testid": `${testIdPrefix}-width-wrapper`, className: "relative z-[1] inline-flex flex-none overflow-hidden rounded-full", onPointerEnter: () => {
            if (!isExpanded) {
                onInactiveHoverChange(true);
            }
        }, onPointerLeave: () => onInactiveHoverChange(false), style: {
            width: usePersistentLabelLayout
                ? progress.to((value) => persistentBaseWidth + persistentWidthDeltaPx * value)
                : shouldUseContentWidth
                    ? 'auto'
                    : progress.to((value) => MODE_TOGGLE_COLLAPSED_WIDTH_PX +
                        (expandedWidth - MODE_TOGGLE_COLLAPSED_WIDTH_PX) * value),
        }, children: _jsx("span", { ref: contentRef, className: "inline-flex w-max", children: children(progress, setPersistentBaseWidth) }) }));
}
function ModeToggleGroupInner({ items, value, onValueSelect, appearance = 'default', labelVisibility = 'active', overlayKind = 'menu', menuOpen, onMenuOpenChange, matchMenuToContainerWidth = false, layoutContainerRef, containerSelector, testIdPrefix = 'mode-toggle', }) {
    const framed = appearance !== 'default';
    const accentFrame = appearance === 'framed-accent';
    const isItemOn = useCallback((item) => item.value === value || (item.activeValues?.includes(value) ?? false), [value]);
    // First matching item wins (start-to-end precedence, matching the prior
    // two-item behavior where `start` took priority over `end`).
    const activeIndex = items.findIndex(isItemOn);
    const activeItem = activeIndex >= 0 ? items[activeIndex] : null;
    const hasActiveToggle = activeItem != null;
    const activeIsShown = activeItem != null && !activeItem.hidden;
    const usePersistentLabelLayout = labelVisibility === 'always';
    const activeHasMenu = (activeItem?.renderMenu !== null && activeItem?.renderMenu !== undefined) ||
        activeItem?.open !== undefined;
    const shouldAnimateToggleWidth = labelVisibility === 'active' || (labelVisibility === 'never' && activeHasMenu);
    const shouldAnimateActiveToggleContent = usePersistentLabelLayout || shouldAnimateToggleWidth;
    const persistentActiveWidthDeltaPx = usePersistentLabelLayout && !framed && activeHasMenu ? MODE_TOGGLE_CHEVRON_WIDTH_PX : 0;
    const [activeToggleWidth, setActiveToggleWidth] = useState(MODE_TOGGLE_COLLAPSED_WIDTH_PX);
    // Persistent-label variant tracks every toggle's width since none collapse.
    const [toggleWidths, setToggleWidths] = useState(() => items.map(() => MODE_TOGGLE_COLLAPSED_WIDTH_PX));
    const [inactiveToggleHovered, setInactiveToggleHovered] = useState(false);
    const transferChevronSpace = framed && usePersistentLabelLayout;
    const setToggleWidthAt = useCallback((index, width) => {
        setToggleWidths((prev) => {
            if (prev[index] === width)
                return prev;
            const next = prev.slice();
            next[index] = width;
            return next;
        });
    }, []);
    // Stable per-index width reporters; identity only changes when the toggle
    // count does, so the width-measuring effect doesn't re-run every render.
    const toggleCount = items.length;
    const widthChangeHandlers = useMemo(() => Array.from({ length: toggleCount }, (_, index) => (width) => {
        setToggleWidthAt(index, width);
    }), [toggleCount, setToggleWidthAt]);
    const widthAt = useCallback((index) => toggleWidths[index] ?? MODE_TOGGLE_COLLAPSED_WIDTH_PX, [toggleWidths]);
    // Number of shown toggles positioned before the active one — drives both
    // indicator offsets (collapsed-width steps for control layout, measured
    // widths for the persistent-label layout).
    const shownBeforeActive = useMemo(() => {
        if (activeIndex <= 0)
            return 0;
        return items.slice(0, activeIndex).filter((item) => !item.hidden).length;
    }, [items, activeIndex]);
    const shownItemCount = useMemo(() => items.filter((item) => !item.hidden).length, [items]);
    const shownInactiveCount = shownItemCount - (activeIsShown ? 1 : 0);
    // Control layout: inactive toggles collapse to their icon-only width, and the
    // indicator spans the active toggle (100% minus the collapsed inactive ones).
    const inactiveToggleWidth = hasActiveToggle && activeIsShown ? MODE_TOGGLE_COLLAPSED_WIDTH_PX * shownInactiveCount : 0;
    const controlActiveToggleX = MODE_TOGGLE_COLLAPSED_WIDTH_PX * shownBeforeActive;
    const indicatorLayoutWidth = hasActiveToggle
        ? `calc(100% - ${inactiveToggleWidth + (framed ? 4 : 0)}px)`
        : 0;
    // Persistent-label variant keeps every toggle full width, so the indicator is
    // sized/positioned from the prefix sum of measured per-toggle widths.
    const persistentLabelActiveToggleX = useMemo(() => {
        if (activeIndex <= 0)
            return 0;
        return items
            .slice(0, activeIndex)
            .reduce((sum, item, index) => sum + (item.hidden ? 0 : widthAt(index)), 0);
    }, [items, activeIndex, widthAt]);
    const persistentLabelActiveToggleWidth = activeIsShown ? widthAt(activeIndex) : 0;
    const activeToggleX = usePersistentLabelLayout
        ? persistentLabelActiveToggleX
        : controlActiveToggleX;
    const { containerRef, indicatorStyle, widthAnimationProps } = useModeToggleAnimation({
        activeIndex,
        activeX: activeToggleX,
        activeWidth: usePersistentLabelLayout ? persistentLabelActiveToggleWidth : activeToggleWidth,
        layoutWidth: indicatorLayoutWidth,
        hasActiveToggle,
        persistentLabels: usePersistentLabelLayout,
        animateWidth: shouldAnimateToggleWidth,
        framed,
    });
    const renderToggle = (item, index) => (animationProgress, onBaseWidthChange) => {
        const externalMenuProps = item.open === undefined
            ? {}
            : {
                open: item.open,
                onOpenChange: item.onOpenChange,
            };
        const localMenuProps = menuOpen === undefined
            ? { onOpenChange: onMenuOpenChange }
            : { open: menuOpen, onOpenChange: onMenuOpenChange ?? NOOP_MENU_OPEN_CHANGE };
        const menuProps = item.renderMenu === null || item.renderMenu === undefined
            ? externalMenuProps
            : localMenuProps;
        const toggle = (_jsx(ModeToggle, { label: item.label, ariaLabel: item.ariaLabel, icon: item.icon, isOn: transferChevronSpace ? index === activeIndex : isItemOn(item), onActivate: () => onValueSelect(item.value), offTooltip: item.offTooltip, offTooltipSubtitle: item.offTooltipSubtitle, lockedReason: item.lockedReason, allowMenuWhenLocked: item.allowMenuWhenLocked, renderMenu: item.renderMenu, ...menuProps, animationProgress: animationProgress, animateChevronWidth: transferChevronSpace, onBaseWidthChange: transferChevronSpace ? onBaseWidthChange : undefined, labelVisibility: labelVisibility, overlayKind: overlayKind, matchMenuToContainerWidth: matchMenuToContainerWidth, appearance: appearance, layoutContainerRef: layoutContainerRef, containerSelector: containerSelector, testIdPrefix: testIdPrefix }));
        return item.renderToggle?.(toggle) ?? toggle;
    };
    // oxlint-disable-next-line pplx/no-private-design-tokens -- The framed track uses the private inset shadow.
    const framedTrackShadow = accentFrame ? 'shadow-inner-sm-accent' : 'shadow-inner-sm';
    return (_jsxs("div", { ref: containerRef, className: framed
            ? classNames('relative flex shrink-0 items-center gap-0 rounded-full p-0.5 transition-[background-color,box-shadow] duration-300 motion-reduce:transition-none', shownItemCount > 1 && [
                accentFrame ? 'bg-super-recessed' : 'bg-recessed',
                framedTrackShadow,
            ])
            : `relative flex shrink-0 items-center gap-0 rounded-full transition-colors duration-quick dark:bg-base ${inactiveToggleHovered ? 'bg-soft' : 'bg-subtle'}`, children: [_jsx(animated.div, { "aria-hidden": true, "data-testid": `${testIdPrefix}-indicator`, className: framed
                    ? classNames('pointer-events-none absolute inset-y-0.5 z-0 rounded-full bg-raised forced-colors:border forced-colors:border-[CanvasText]', surfaceStyles.fill, surfaceStyles.shadow, accentFrame && surfaceStyles.accent, accentFrame ? 'after:shadow-sm-accent' : 'after:shadow-sm')
                    : 'pointer-events-none absolute top-0 z-0 h-full rounded-full border bg-raised dark:bg-soft', style: indicatorStyle }), items.map((item, index) => item.hidden ? null : (_jsx(ToggleWidthWrapper, { isExpanded: shouldAnimateActiveToggleContent &&
                    (transferChevronSpace ? index === activeIndex : isItemOn(item)), usePersistentLabelLayout: usePersistentLabelLayout, activeWidthDeltaPx: transferChevronSpace
                    ? (item.renderMenu !== null && item.renderMenu !== undefined) ||
                        item.open !== undefined
                        ? MODE_TOGGLE_CHEVRON_WIDTH_PX
                        : 0
                    : persistentActiveWidthDeltaPx, animateChevronWidth: transferChevronSpace, ...widthAnimationProps, onActiveWidthChange: setActiveToggleWidth, onWidthChange: widthChangeHandlers[index] ?? NOOP_WIDTH_CHANGE, onInactiveHoverChange: setInactiveToggleHovered, testIdPrefix: testIdPrefix, children: renderToggle(item, index) }, item.value)))] }));
}
export const ModeToggleGroup = memo(ModeToggleGroupInner);
//# sourceMappingURL=ModeToggleGroup.js.map